import { INPUT_CLASS, LABEL_CLASS } from '@/components/common/node-view-settings-panel';
import type { Node as PMNode } from '@tiptap/pm/model';

export const ITLAB_TAB = {
    INSTRUCTIONS: 'instructions',
    MODEL_SOLUTION: 'model-solution',
    VM_IMAGES: 'vm-images',
    TASKS: 'tasks',
    VM_PREVIEW: 'vm-preview',
} as const;

export type ITLabTab = (typeof ITLAB_TAB)[keyof typeof ITLAB_TAB];

export const ITLAB_TAB_LIST: ReadonlyArray<{ value: ITLabTab; label: string }> = [
    { value: ITLAB_TAB.INSTRUCTIONS, label: 'Instructions' },
    { value: ITLAB_TAB.VM_IMAGES, label: 'VM images' },
    { value: ITLAB_TAB.TASKS, label: 'Tasks' },
    { value: ITLAB_TAB.VM_PREVIEW, label: 'VM preview' },
];

export const ITLAB_SECTION = {
    INSTRUCTIONS: ITLAB_TAB.INSTRUCTIONS,
    MODEL_SOLUTION: ITLAB_TAB.MODEL_SOLUTION,
    OPTIONAL_TASKS: 'optional-tasks',
    VM_IMAGES: ITLAB_TAB.VM_IMAGES,
    TASKS: ITLAB_TAB.TASKS,
    VM_PREVIEW: ITLAB_TAB.VM_PREVIEW,
} as const;

export const ITLAB_SECTION_ATTR = 'data-itlab-section';

export function findZyITLabChildIndex(labNode: PMNode, childType: string): number {
    for (let i = 0; i < labNode.childCount; i++) {
        if (labNode.child(i).type.name === childType) {
            return i;
        }
    }
    return -1;
}

export function getZyITLabTaskRegion(labNode: PMNode, taskChildIndex: number): 'optional' | 'tasks' {
    const vmImagesIndex = findZyITLabChildIndex(labNode, 'zyITLabVmImages');
    if (vmImagesIndex < 0) {
        return 'tasks';
    }
    return taskChildIndex < vmImagesIndex ? 'optional' : 'tasks';
}

export function hasZyITLabOptionalTask(labNode: PMNode): boolean {
    const vmImagesIndex = findZyITLabChildIndex(labNode, 'zyITLabVmImages');
    if (vmImagesIndex < 0) {
        return false;
    }
    for (let i = 0; i < vmImagesIndex; i++) {
        if (labNode.child(i).type.name === 'zyITLabTask') {
            return true;
        }
    }
    return false;
}

export function getZyITLabTasksInRegion(labNode: PMNode, region: 'optional' | 'tasks'): PMNode[] {
    const vmImagesIndex = findZyITLabChildIndex(labNode, 'zyITLabVmImages');
    const tasks: PMNode[] = [];
    labNode.forEach((child, _offset, childIndex) => {
        if (child.type.name !== 'zyITLabTask') {
            return;
        }
        const inOptional = vmImagesIndex >= 0 && childIndex < vmImagesIndex;
        if ((region === 'optional' && inOptional) || (region === 'tasks' && !inOptional)) {
            tasks.push(child);
        }
    });
    return tasks;
}

export function getZyITLabTaskIndexInRegion(
    labNode: PMNode,
    taskChildIndex: number,
    region: 'optional' | 'tasks',
): { nodeIndex: number; totalNodes: number } {
    const vmImagesIndex = findZyITLabChildIndex(labNode, 'zyITLabVmImages');
    let nodeIndex = -1;
    let totalNodes = 0;

    labNode.forEach((child, _offset, childIndex) => {
        if (child.type.name !== 'zyITLabTask') {
            return;
        }
        const inOptional = vmImagesIndex >= 0 && childIndex < vmImagesIndex;
        const inRegion = (region === 'optional' && inOptional) || (region === 'tasks' && !inOptional);
        if (!inRegion) {
            return;
        }
        if (childIndex === taskChildIndex) {
            nodeIndex = totalNodes;
        }
        totalNodes++;
    });

    return { nodeIndex, totalNodes };
}

export const VM_INSTANCE_TYPE_OPTIONS = ['small', 'medium', 'large', 'xlarge', 'c6i.xlarge'] as const;

export type VmInstanceType = (typeof VM_INSTANCE_TYPE_OPTIONS)[number];

export const DEFAULT_VM_INSTANCE_TYPE: VmInstanceType = 'small';

export const DEFAULT_VM_IMAGE_NAME = 'windows-server-2022-active-directory-base-zywin01-july23';

export function countChildType(node: PMNode, typeName: string): number {
    let count = 0;
    node.forEach(child => {
        if (child.type.name === typeName) {
            count++;
        }
    });
    return count;
}

export function getChildText(node: PMNode, typeName: string): string {
    let text = '';
    node.forEach(child => {
        if (child.type.name === typeName) {
            text = child.textContent;
        }
    });
    return text;
}

export function sumGoalPoints(stepNode: PMNode): number {
    let total = 0;
    stepNode.forEach(child => {
        if (child.type.name === 'zyITLabGoal') {
            total += Number(child.attrs.points) || 0;
        }
    });
    return total;
}

interface InstanceTypeSelectProps {
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
}

export function InstanceTypeSelect({ value, onChange, disabled }: InstanceTypeSelectProps) {
    return (
        <div>
            <label
                className={LABEL_CLASS}
                contentEditable={false}
            >
                Instance type
            </label>
            <select
                className={INPUT_CLASS}
                value={value}
                onChange={e => onChange(e.target.value)}
                disabled={disabled}
                contentEditable={false}
            >
                {VM_INSTANCE_TYPE_OPTIONS.map(option => (
                    <option
                        key={option}
                        value={option}
                    >
                        {option}
                    </option>
                ))}
            </select>
        </div>
    );
}
